import { LogOut, Menu, X, ChevronDown, Sun, Moon } from "lucide-react";
import { useState, useEffect } from "react";
import { Brand } from "../components/Brand";
import { modules } from "../config/modules";
import { tenantService } from "../services/tenantService";

export function AppLayout({ session, active, setActive, onLogout, children, headerAction }) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 900);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => (typeof window !== "undefined" && window.innerWidth < 900) ? true : false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [dark, setDark] = useState(() => document.documentElement.getAttribute("data-theme") === "dark");
  const [tenantName, setTenantName] = useState("");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 900;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarCollapsed(false);
      } else {
        setSidebarCollapsed(true);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (session?.tenantId) {
      tenantService.get(session.tenantId)
        .then((data) => setTenantName(data?.tenantName || data?.name || ""))
        .catch(() => setTenantName(""));
    } else {
      setTenantName("");
    }
  }, [session?.tenantId]);

  const allowed = session?.allowedModules?.length ? session.allowedModules : modules.map((module) => module.id);
  const visibleModules = modules.filter((module) => allowed.includes(module.id));
  const currentModule = modules.find((module) => module.id === active);
  const title = currentModule?.header || currentModule?.label;

  const handleHamburgerClick = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div className="app-shell" onClick={() => setProfileOpen(false)}>
      <header className={`topbar ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
        <button
          className="hamburger"
          onClick={(e) => {
            e.stopPropagation();
            handleHamburgerClick();
          }}
          title="Toggle menu"
          aria-label="Toggle menu"
        >
          <Menu size={20} />
        </button>
        <div className="topbar-brand">
          <Brand />
          {tenantName && <span className="brand-separator">| Welcome, {tenantName}</span>}
        </div>
        <div className="topbar-actions">
          <button className="theme-toggle" onClick={(e) => { e.stopPropagation(); setDark(!dark); }} title={dark ? "Switch to light mode" : "Switch to dark mode"}>
            {dark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <div className="profile-pill" onClick={(e) => { e.stopPropagation(); setProfileOpen(!profileOpen); }}>
            <span>{session.username}</span>
            <ChevronDown size={14} className="profile-arrow" />
            {profileOpen && (
              <div className="profile-dropdown" onClick={(e) => e.stopPropagation()}>
                <button onClick={onLogout}><LogOut size={14} /> Logout</button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="app-body">
        {isMobile && !sidebarCollapsed && (
          <div
            className="sidebar-overlay"
            onClick={() => setSidebarCollapsed(true)}
          />
        )}
        <aside className={`sidebar ${sidebarCollapsed ? "collapsed" : ""}`}>
          <nav>
            {visibleModules.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                className={active === id ? "active" : ""}
                onClick={() => {
                  setActive(id);
                  if (isMobile) {
                    setSidebarCollapsed(true);
                  }
                }}
                title={label}
              >
                <Icon size={18} />
                <span>{label}</span>
              </button>
            ))}
          </nav>
        </aside>

        <main className="workspace">
          <header className="content-header">
            <h1>{title}</h1>
            {headerAction}
          </header>
          {children}
        </main>
      </div>
    </div>
  );
}
